import * as React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { confirmPopup } from 'primereact/confirmpopup';
import { parseISO, format } from 'date-fns';
import { useFormik } from 'formik';
import { useAxios } from '../../utils/hooks.ts';

// Most-to-least privileged, matching co.newlabs.enums.Role's declaration order on the
// backend - the picker only offers roles from the project's own role onward, same
// convention as the OAuth consent screen's role picker (Authorize.js).
const ROLE_ORDER = ['OWNER', 'ADMIN', 'USER', 'VIEWER'];

// Static ftly_ keys for server-to-server / automation MCP clients (n8n, scripts, etc.)
// that can't do an interactive OAuth consent flow - the counterpart to ConnectedApps.js,
// which lists OAuth grants instead. Only ADMIN/OWNER project members can manage these
// (enforced server-side by ApiKeyService.validateProjectAdmin).
export default function ApiKeys({ projectId, role, displayToast }) {
    const [ keys, setKeys ] = React.useState([]);
    const [ fetchFlag, setFetchFlag ] = React.useState(false);
    const [ createVisible, setCreateVisible ] = React.useState(false);
    const [ saving, setSaving ] = React.useState(false);
    const [ revealedSecret, setRevealedSecret ] = React.useState(null);
    const axiosInstance = useAxios();

    const roleOptions = ROLE_ORDER.slice(ROLE_ORDER.indexOf(role)).map((r) => ({ name: r, role: r }));

    const flipFetchFlag = () => {
        setFetchFlag(!fetchFlag);
    }

    React.useEffect(() => {
        axiosInstance.current.get("/api-keys", { params: { projectId } }).then((response) => {
            setKeys(response.data);
        })
        .catch((err) => {
            console.log(err);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchFlag, projectId]);

    const toggleCreateVisible = () => {
        formik.resetForm();
        setCreateVisible(!createVisible);
    }

    const formik = useFormik({
        initialValues: { name: '', role: role },
        validate: (data) => {
            let errors = {};
            if (!data.name) {
                errors.name = 'Name is required';
            }
            return errors;
        },
        onSubmit: (data) => {
            setSaving(true);
            axiosInstance.current.post("/api-keys", { projectId, name: data.name, role: data.role }).then((response) => {
                setCreateVisible(false);
                setRevealedSecret(response.data);
                flipFetchFlag();
            }).catch((err) => {
                console.log(err);
                displayToast({ severity: 'error', summary: 'Error!', detail: 'Unable to create API key' });
            }).finally(() => {
                setSaving(false);
            })
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;

    const rotate = (key) => {
        axiosInstance.current.put("/api-keys/" + key.apiKeyId + "/rotate").then((response) => {
            setRevealedSecret(response.data);
            flipFetchFlag();
        })
        .catch((err) => {
            console.log(err);
            displayToast({ severity: 'error', summary: 'Error!', detail: 'Unable to rotate API key' });
        })
    }

    const revoke = (key) => {
        axiosInstance.current.delete("/api-keys/" + key.apiKeyId).then(() => {
            flipFetchFlag();
        })
        .catch((err) => {
            console.log(err);
            displayToast({ severity: 'error', summary: 'Error!', detail: 'Unable to revoke API key' });
        })
    }

    const confirmRotate = (key, e) => {
        confirmPopup({
            target: e.currentTarget,
            message: "Rotating this key immediately invalidates the old secret. Any client using it will need the new one.",
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            accept: () => rotate(key),
            reject: () => {}
        })
    }

    const confirmRevoke = (key, e) => {
        confirmPopup({
            target: e.currentTarget,
            message: "Are you sure you want to revoke this API key? It will stop working immediately.",
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            accept: () => revoke(key),
            reject: () => {}
        })
    }

    const copySecret = () => {
        navigator.clipboard.writeText(revealedSecret.secret);
        displayToast({ severity: 'info', detail: 'Copied!' });
    }

    const statusBody = (rowData) => <Tag severity={rowData.revoked ? 'danger' : 'success'} value={rowData.revoked ? 'Revoked' : 'Active'} />;
    const createdBody = (rowData) => rowData.createdAt ? format(parseISO(rowData.createdAt), "MMMM dd, yyyy h:mm a") : '';
    const lastUsedBody = (rowData) => rowData.lastUsedAt ? format(parseISO(rowData.lastUsedAt), "MMMM dd, yyyy h:mm a") : 'Never';
    const keyBody = (rowData) => <code>{rowData.keyPrefix}…</code>;
    const actionsBody = (rowData) => !rowData.revoked && (
        <>
            <Button className="p-button-outlined p-button-secondary" icon="pi pi-refresh" style={{ marginRight: 10 }} onClick={(e) => confirmRotate(rowData, e)} />
            <Button className="p-button-outlined p-button-danger" icon="pi pi-ban" onClick={(e) => confirmRevoke(rowData, e)} />
        </>
    );

    return (
        <div className="mt10">
            <Dialog header="API key created" visible={!!revealedSecret} onHide={() => setRevealedSecret(null)}>
                <div style={{ maxWidth: 480 }}>
                    <p>Copy this key now - for security reasons, you won't be able to see it again.</p>
                    <div className="code-block flex-row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <pre style={{ margin: 0 }}>{revealedSecret?.secret}</pre>
                        <Button className="p-button-text" icon="pi pi-copy" onClick={copySecret} />
                    </div>
                    <p className="pt20">Use it as a Bearer token when connecting an MCP client (e.g. n8n) to <code>https://www.featurerollout.com/api/mcp</code>.</p>
                </div>
            </Dialog>

            <Dialog header="Create API Key" visible={createVisible} onHide={toggleCreateVisible}>
                <div className="form">
                    <form onSubmit={formik.handleSubmit} className="p-fluid" autoComplete="off" style={{ minWidth: 320 }}>
                        <div className="field m20">
                            <span className="p-float-label">
                                <InputText id="name" name="name" maxLength={100} value={formik.values.name} onChange={formik.handleChange} autoFocus className={classNames({ 'p-invalid': isFormFieldValid('name') })} />
                                <label htmlFor="name" className={classNames({ 'p-error': isFormFieldValid('name') })}>Name *</label>
                            </span>
                            {getFormErrorMessage('name')}
                        </div>
                        <div className="field m20">
                            <label htmlFor="role" className="audit-details">Access level</label>
                            <Dropdown id="role" optionLabel="name" optionValue="role" value={formik.values.role} options={roleOptions} onChange={(e) => formik.setFieldValue('role', e.value)} />
                        </div>
                        <div className="flex-row center mt20">
                            <Button style={{ margin: 20 }} type="submit" label="Create Key" icon="pi pi-save" loading={saving} />
                        </div>
                    </form>
                </div>
            </Dialog>

            <div className="secondary-title mt10 flex-row" style={{ justifyContent: 'space-between' }}>
                <div>API Keys</div>
                <Button label="Create API Key" icon="pi pi-plus-circle" className="p-button-text" onClick={toggleCreateVisible} />
            </div>
            <p className="audit-details">Static keys for MCP clients that can't complete an interactive login, like n8n or other automation tools.</p>
            <DataTable value={keys} responsiveLayout="stack" size="small" dataKey="apiKeyId" emptyMessage="No API keys yet">
                <Column field="name" header="Name"></Column>
                <Column body={keyBody} header="Key"></Column>
                <Column field="role" header="Access Level"></Column>
                <Column body={statusBody} header="Status"></Column>
                <Column body={createdBody} header="Created"></Column>
                <Column body={lastUsedBody} header="Last Used"></Column>
                <Column body={actionsBody}></Column>
            </DataTable>
        </div>
    )
}
